import { Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperatorPicker } from "@/components/wallet/OperatorPicker";
import { PhoneInput } from "@/components/common/PhoneInput";
import { parsePhone, toE164, type Country } from "@/lib/countries";
import { useAuth } from "@/hooks/useAuth";
import { useDeposit } from "@/hooks/useWallet";
import { MIN_DEPOSIT, calculateFee, totalWithFee, operatorsForCountry } from "@/lib/constants";
import { formatFcfa } from "@/lib/format";
import { toast } from "sonner";

// NB : le provider Geniuspay est temporairement masqué côté UI. Le hook
// useDepositGeniuspay, la edge function geniuspay-deposit et le webhook
// associé restent en place pour réactivation rapide quand l'infra sera
// prête (cf. supabase/migrations/00000000000003_add_payment_provider.sql).

const phoneSchema = z.object({
  country: z.object({ code: z.string(), name: z.string(), dial: z.string(), flag: z.string() }),
  local: z.string().min(6).max(15),
});

const schema = z.object({
  amount: z.coerce.number().int().min(MIN_DEPOSIT),
  phone: phoneSchema,
  operator: z.string().min(1, "Choisis un opérateur"),
});
type FormValues = z.infer<typeof schema>;

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000];

export function Deposit() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const deposit = useDeposit();

  const initialPhone = parsePhone(profile?.phone ?? null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: 1000,
      phone: initialPhone,
      operator: "",
    },
  });

  const phone = form.watch("phone");
  const operator = form.watch("operator");
  const amount = form.watch("amount");

  const countryOperators = operatorsForCountry(phone?.country?.code ?? "");
  if (operator && !countryOperators.find((o) => o.id === operator)) {
    form.setValue("operator", "");
  }

  async function onSubmit(data: FormValues) {
    try {
      const phoneE164 = toE164(data.phone.country as Country, data.phone.local);
      const r = await deposit.mutateAsync({
        amount: data.amount,
        phone: phoneE164,
        operator: data.operator,
      });
      if (!r.checkout_url) {
        toast.error("PayDunya n'a pas retourné d'URL de paiement");
        return;
      }
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur paiement");
    }
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/wallet"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Déposer</h1>
        <div className="size-8" />
      </header>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="space-y-2">
          <Label>Montant</Label>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="0"
            min={MIN_DEPOSIT}
            {...form.register("amount", { valueAsNumber: true })}
            className="text-2xl font-bold h-14 num"
          />
          <p className="text-2xs text-muted-foreground tabular-nums">
            Minimum {formatFcfa(MIN_DEPOSIT)}
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {QUICK_AMOUNTS.map((a) => (
              <Button
                key={a}
                type="button"
                variant={amount === a ? "default" : "secondary"}
                size="sm"
                onClick={() => form.setValue("amount", a)}
              >
                {a >= 1000 ? `${a / 1000}k` : a}
              </Button>
            ))}
          </div>
          {form.formState.errors.amount && <p className="text-xs text-destructive">{form.formState.errors.amount.message}</p>}
        </div>

        {amount >= MIN_DEPOSIT && (
          <div className="rounded-xl border border-white/[0.06] bg-card p-3 space-y-1.5 text-xs">
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Montant crédité sur ton wallet</span>
              <span className="font-semibold text-foreground num">{formatFcfa(amount)}</span>
            </div>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Frais opérateur (4 %)</span>
              <span className="font-medium num">{formatFcfa(calculateFee(amount))}</span>
            </div>
            <div className="h-px bg-white/[0.06] my-1" />
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total à payer</span>
              <span className="text-base font-bold text-primary num">{formatFcfa(totalWithFee(amount))}</span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Numéro de téléphone</Label>
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value as { country: Country; local: string }}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.phone?.local && (
            <p className="text-xs text-destructive">{form.formState.errors.phone.local.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Opérateur</Label>
          <OperatorPicker
            countryCode={phone?.country?.code ?? "SN"}
            value={operator || null}
            onChange={(op: string) => form.setValue("operator", op, { shouldValidate: true })}
          />
          {form.formState.errors.operator && (
            <p className="text-xs text-destructive">{form.formState.errors.operator.message}</p>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={deposit.isPending}
          disabled={!operator}
        >
          {t("wallet.submitDeposit")}
        </Button>
      </form>
    </div>
  );
}
